import React, { useState } from 'react';
import { Platform, Pressable, View, ScrollView } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

// Same mock action strip as chat message actions — bookmark highlighted
// without pulse animation so the empty state stays calm.
const ACTION_ICONS = ['copy', 'thumbs-up', 'thumbs-down', 'bookmark', 'arrow-rotate-right', 'share-nodes'];

/** ~two 460px columns + gap + bookmarks rail — stack below this width */
const MAGAZINE_SPLIT_MIN_WIDTH = 1180;

function TapThisActionRow({ colors, t, compact }) {
  const cell = compact ? 28 : 32;
  const iconSz = compact ? 11 : 13;

  return (
    <View style={{ marginTop: compact ? 10 : 14, paddingTop: compact ? 10 : 12, borderTopWidth: 1, borderTopColor: colors.border_color_secondary }}>
      <Text size="xxs" color="tertiary" weight="semibold" style={{ marginBottom: compact ? 6 : 8, letterSpacing: 0.3 }}>
        {t('pages.bookmarks.emptyMagazineTapHint')}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          gap: compact ? 3 : 4,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: compact ? 'flex-start' : 'flex-end',
        }}
      >
        {ACTION_ICONS.map((name) => {
          const isBookmark = name === 'bookmark';
          return (
            <View
              key={name}
              style={{
                width: cell,
                height: cell,
                borderRadius: 6,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isBookmark ? colors.solid_color_secondary : 'transparent',
                borderWidth: isBookmark ? 1 : 0,
                borderColor: isBookmark ? colors.border_color_primary : 'transparent',
              }}
            >
              <Icon
                name={name}
                size={iconSz}
                color={isBookmark ? colors.text_color_primary : colors.text_color_tertiary}
                isSolid={isBookmark}
              />
            </View>
          );
        })}
      </View>
    </View>
  );
}

/**
 * Full-page empty state for Bookmarks when the user has zero saved messages
 * (All bookmarks scope, no active search). Quiet editorial + preview card.
 */
const BookmarksEmptyMagazine = ({ onStartChat, style: styleProp }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, windowWidth, windowHeight } = useResponsiveness();
  const stacked = isXS || windowWidth < MAGAZINE_SPLIT_MIN_WIDTH;
  const tight = isXS;
  const shortViewport = windowHeight < 680;
  const needsScroll = stacked || shortViewport;
  const padH = tight ? 16 : stacked ? 24 : 32;
  const padV = tight ? 28 : stacked ? 36 : 48;
  const colGap = tight ? 24 : stacked ? 32 : 48;
  const headlineSize = stacked ? 'xxl' : 'xxxl';
  const headlineLineHeight = stacked ? 30 : 36;
  const leadSize = tight ? 's' : 'm';
  const cardPad = tight ? 14 : 16;
  const [primaryHovered, setPrimaryHovered] = useState(false);

  const reasons = [
    t('pages.bookmarks.emptyMagazineReason1'),
    t('pages.bookmarks.emptyMagazineReason2'),
    t('pages.bookmarks.emptyMagazineReason3'),
    t('pages.bookmarks.emptyMagazineReason4'),
  ];

  const inner = (
    <View
      style={[
        {
          width: '100%',
          maxWidth: stacked ? '100%' : 960,
          alignSelf: 'center',
          paddingHorizontal: padH,
          paddingVertical: padV,
          flexDirection: stacked ? 'column' : 'row',
          gap: colGap,
          alignItems: stacked ? 'stretch' : 'center',
          justifyContent: 'center',
        },
        styleProp,
      ]}
    >
      {/* Left column — editorial */}
      <View style={{ flex: 1, maxWidth: 420, minWidth: 0, width: stacked ? '100%' : undefined }}>
        <Text
          size={headlineSize}
          weight="bold"
          style={{ letterSpacing: -0.5, lineHeight: headlineLineHeight, marginBottom: tight ? 10 : 12 }}
        >
          {t('pages.bookmarks.emptyMagazineHeadline')}
        </Text>
        <Text size={leadSize} color="secondary" style={{ lineHeight: tight ? 20 : 22, marginBottom: tight ? 18 : 22 }}>
          {t('pages.bookmarks.emptyMagazineLead')}
        </Text>

        <View style={{ gap: tight ? 8 : 10, marginBottom: tight ? 20 : 24 }}>
          {reasons.map((r, idx) => (
            <View key={`empty-magazine-reason-${idx}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 9,
                  backgroundColor: colors.solid_color_secondary,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="check" size={9} color={colors.text_color_secondary} />
              </View>
              <Text size="s" color="secondary">
                {r}
              </Text>
            </View>
          ))}
        </View>

        <Pressable
          onHoverIn={() => setPrimaryHovered(true)}
          onHoverOut={() => setPrimaryHovered(false)}
          onPress={onStartChat}
          style={{
            alignSelf: tight ? 'stretch' : 'flex-start',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            paddingHorizontal: tight ? 16 : 18,
            minHeight: tight ? 48 : 40,
            height: tight ? undefined : 40,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: primaryHovered ? colors.border_color_primary : colors.border_color_secondary,
            backgroundColor: primaryHovered ? colors.solid_color_tertiary : colors.solid_color_block,
            ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'background-color 0.12s ease, border-color 0.12s ease' }),
          }}
        >
          <Icon name="pen-to-square" size={13} color={colors.text_color_primary} />
          <Text size="s" weight="semibold">
            {t('pages.messages.actions.startNewChat')}
          </Text>
        </Pressable>
      </View>

      {/* Right column — mockup */}
      <View
        style={{
          flex: 1,
          maxWidth: 420,
          minWidth: 0,
          width: stacked ? '100%' : undefined,
          alignItems: stacked ? 'stretch' : 'center',
          justifyContent: 'center',
        }}
      >
        <View
          style={{
            width: '100%',
            padding: cardPad,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: colors.border_color_secondary,
            backgroundColor: colors.solid_color_block,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <Text size="xxs" weight="bold" color="tertiary" style={{ letterSpacing: 0.5 }}>
              {t('pages.bookmarks.emptyMagazinePreviewLabel')}
            </Text>
            <Icon name="bookmark" size={12} color={colors.text_color_tertiary} isSolid />
          </View>

          <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
            <View
              style={{
                width: 3,
                height: 14,
                marginTop: 4,
                borderRadius: 2,
                backgroundColor: colors.border_color_input || colors.border_color_secondary,
              }}
            />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text size="s" weight="semibold" style={{ marginBottom: 4 }}>
                {t('pages.bookmarks.emptyMagazineSampleTitle')}
              </Text>
              <Text size="xs" color="secondary" numberOfLines={2} style={{ lineHeight: 18, marginBottom: 10 }}>
                {t('pages.bookmarks.emptyMagazineSampleSnippet')}
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Icon name="message-lines" size={10} color={colors.text_color_tertiary} />
                  <Text size="xxs" color="tertiary">{t('pages.bookmarks.emptyMagazineSampleChat')}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Icon name="sparkles" size={10} color={colors.text_color_tertiary} />
                  <Text size="xxs" color="tertiary">{t('pages.bookmarks.emptyMagazineSampleModel')}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Icon name="clock" size={10} color={colors.text_color_tertiary} />
                  <Text size="xxs" color="tertiary">{t('pages.bookmarks.emptyMagazineSampleDate')}</Text>
                </View>
              </View>
            </View>
          </View>

          <TapThisActionRow colors={colors} t={t} compact={tight} />
        </View>
      </View>
    </View>
  );

  // On narrow viewports the magazine layout is tall — scroll so nothing
  // is clipped under the chrome bar or home indicator.
  if (needsScroll) {
    return (
      <ScrollView
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: tight ? 32 : 28,
          justifyContent: shortViewport && !stacked ? 'flex-start' : undefined,
        }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {inner}
      </ScrollView>
    );
  }

  return (
    <View style={{ flex: 1, width: '100%', minHeight: 0 }}>
      {inner}
    </View>
  );
};

export default BookmarksEmptyMagazine;
